import {
  getParsedCookie,
  removeCookie,
  SameSite,
  setStringifiedCookie,
} from '../helpers/cookie';
import {
  CookieConsentStore,
  CookieConsentStoreState,
  CookieConsentStoreCookie,
} from './useCookieConsentStore.types';
import {
  CONSENT_COOKIE_NAME,
  GTM_STORE_VERSION,
  getConsentExpirationDate,
  sendGTMConsentUpdate,
} from './useCookieConsentStore.utils';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const initialState: CookieConsentStoreState = {
  isConsentPopupOpen: true,
  isConsentModalOpen: false,
  consent: undefined,
  cookieDomain: undefined,
};

/**
 * Custom hook to manage Google Tag Manager (GTM) consent state using Zustand and cookies for persistence.
 *
 * @returns {GTMStore} The GTM store with methods to manage consent state.
 *
 * @remarks
 * This store is intended to be used alongside the CookieConsentModal component from `@onbeam/features` to manage GTM consent.
 */
export const useCookieConsentStore = create<CookieConsentStore>()(
  persist(
    (set) => ({
      ...initialState,
      closeConsentPopup: () =>
        set((state) => (state.consent ? { isConsentPopupOpen: false } : {})),
      openConsentModal: () => set({ isConsentModalOpen: true }),
      closeConsentModal: () => set({ isConsentModalOpen: false }),
      updateConsent: (consent, cookieDomain) => {
        sendGTMConsentUpdate(consent);
        set({
          isConsentPopupOpen: false,
          isConsentModalOpen: false,
          consent,
          cookieDomain: cookieDomain || window.location.hostname,
        });
      },
    }),
    {
      name: CONSENT_COOKIE_NAME,
      version: GTM_STORE_VERSION,
      storage: {
        getItem: (key) => {
          const cookie = getParsedCookie<CookieConsentStoreCookie>(key);

          if (!cookie?.state) {
            /**
             * To remove old cookies placed on the exact domain.
             * TODO: remove this on 04-12-2025 for Leon's peace of mind.
             */
            removeCookie(key);
            return { version: GTM_STORE_VERSION, state: initialState };
          }

          return {
            version: cookie.version,
            state: {
              ...initialState,
              isConsentPopupOpen: false,
              consent: cookie.state,
            },
          };
        },
        setItem: (key, { state, version }) => {
          if (!state.consent || !state.cookieDomain) return;
          setStringifiedCookie(
            key,
            { version, state: state.consent },
            {
              expiry: getConsentExpirationDate(),
              secure: window.location.protocol === 'https:',
              domain: state.cookieDomain,
              sameSite: SameSite.Strict,
            },
          );
        },
        removeItem: removeCookie,
      },
    },
  ),
);
